import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Key, Plus, Edit, Save, X, Trash2, Activity, AlertTriangle, RotateCcw, Clock, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Calculate time until next A4F quota reset (midnight UTC)
const useA4FResetCountdown = () => {
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateCountdown = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setUTCHours(24, 0, 0, 0); // Next midnight UTC
      
      const diff = midnight.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ hours, minutes, seconds });
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  return countdown;
};
interface APIKey {
  id: string;
  provider: string;
  api_key_encrypted: string;
  is_active: boolean;
  rate_limit_per_minute: number;
  current_usage: number;
  requests_this_minute: number;
  last_reset: string;
  priority_weight: number;
  usage_scope: 'automation' | 'dashboard' | 'all' | 'guest';
  account_name?: string;
  supported_models?: string[];
  subscription_plan?: string;
  created_at: string;
  updated_at: string;
  total_requests?: number;
  total_images?: number;
  success_rate?: number;
  last_used?: string;
  avg_duration_ms?: number;
  daily_images_generated?: number;
  daily_limit?: number;
}

const AdminAPIKeyManagement = () => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<APIKey>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [todayStats, setTodayStats] = useState({ total_calls: 0, total_images: 0 });
  const [forceResetting, setForceResetting] = useState(false);
  const a4fResetCountdown = useA4FResetCountdown();
  const [newKey, setNewKey] = useState({
    provider: '',
    api_key_encrypted: '',
    rate_limit_per_minute: 50,
    priority_weight: 1,
    usage_scope: 'all' as 'automation' | 'dashboard' | 'all',
    account_name: '',
    supported_models: '',
    subscription_plan: 'normal',
    is_active: true
  });

  // Calculate key statistics
  const keyStats = useMemo(() => {
    const exhausted = apiKeys.filter(k => (k.daily_images_generated || 0) >= 9999 || (k.daily_images_generated || 0) >= (k.daily_limit || 300));
    const available = apiKeys.filter(k => k.is_active && (k.daily_images_generated || 0) < (k.daily_limit || 300) && (k.daily_images_generated || 0) < 9999);
    const fresh = apiKeys.filter(k => k.is_active && (k.daily_images_generated || 0) === 0);
    return { exhausted: exhausted.length, available: available.length, fresh: fresh.length };
  }, [apiKeys]);

  const fetchAPIKeys = async () => {
    try {
      setLoading(true);
      // First get masked keys from secure function
      const { data: maskedData, error: maskedError } = await supabase
        .rpc('get_api_keys_masked');

      if (maskedError) {
        console.error('Error fetching API keys:', maskedError);
        toast.error('Failed to load API keys');
        return;
      }

      // Get today's usage statistics with real-time RPM data
      const { data: usageStats, error: usageError } = await supabase
        .rpc('get_api_key_usage_today');

      if (usageError) {
        console.error('Error fetching usage stats:', usageError);
        // Continue without usage stats
      }

      // Get today's total API calls
      const { data: todayData, error: todayError } = await supabase
        .rpc('get_today_api_calls');

      if (!todayError && todayData && todayData.length > 0) {
        setTodayStats({
          total_calls: Number(todayData[0].total_calls) || 0,
          total_images: Number(todayData[0].total_images) || 0
        });
      }

      // Map data - all fields now come directly from get_api_keys_masked RPC
      const mappedData = (maskedData || []).map((key: any) => {
        const usageInfo = usageStats?.find((u: any) => u.api_key_id === key.id);
        
        // Calculate reset time
        const lastReset = new Date(key.rpm_usage_timestamp || key.updated_at);
        const nextReset = new Date(lastReset.getTime() + 60000); // +1 minute
        const now = new Date();
        const secondsUntilReset = Math.max(0, Math.floor((nextReset.getTime() - now.getTime()) / 1000));
        
        // Calculate RPM usage percentage - now comes directly from RPC
        const requestsThisMinute = key.requests_this_minute || 0;
        const rpmPercentage = key.rate_limit_per_minute > 0 
          ? (requestsThisMinute / key.rate_limit_per_minute) * 100 
          : 0;
        
        // Determine color based on usage
        let rpmColor = 'text-green-500';
        if (rpmPercentage >= 80) rpmColor = 'text-red-500';
        else if (rpmPercentage >= 50) rpmColor = 'text-yellow-500';
        
        // Check if key is exhausted at provider level - now comes directly from RPC
        const dailyUsed = key.daily_images_generated || 0;
        const dailyLimit = key.daily_limit || 300;
        const isExhaustedAtProvider = dailyUsed >= 9999 || dailyUsed >= dailyLimit;

        return {
          ...key,
          api_key_encrypted: key.api_key_preview || '************************************',
          usage_scope: key.usage_scope || 'all',
          // These now come directly from the enhanced get_api_keys_masked RPC:
          account_name: key.account_name || null,
          supported_models: key.supported_models || null,
          subscription_plan: key.subscription_plan || 'normal',
          requests_this_minute: requestsThisMinute,
          daily_images_generated: dailyUsed,
          daily_limit: dailyLimit,
          // Usage stats still come from get_api_key_usage_today:
          total_requests: (usageInfo?.total_requests ?? (usageInfo as any)?.requests_made) || 0,
          total_images: (usageInfo?.total_images ?? (usageInfo as any)?.images_generated) || 0,
          success_rate: usageInfo?.success_rate ?? ((((usageInfo as any)?.success_count ?? 0) + ((usageInfo as any)?.error_count ?? 0)) > 0
            ? Math.round((((usageInfo as any)?.success_count ?? 0) / (((usageInfo as any)?.success_count ?? 0) + ((usageInfo as any)?.error_count ?? 0))) * 100)
            : 0),
          last_used: usageInfo?.last_used ?? (usageInfo as any)?.last_used_at ?? null,
          avg_duration_ms: usageInfo?.avg_duration_ms ?? ((((usageInfo as any)?.total_duration_ms ?? 0) > 0 && (((usageInfo as any)?.total_requests ?? (usageInfo as any)?.requests_made) || 0) > 0)
            ? Math.round(Number((usageInfo as any).total_duration_ms) / Number(((usageInfo as any).total_requests ?? (usageInfo as any).requests_made)))
            : 0),
          seconds_until_reset: secondsUntilReset,
          rpm_color: rpmColor,
          rpm_percentage: rpmPercentage,
          is_exhausted_at_provider: isExhaustedAtProvider
        };
      });

      setApiKeys(mappedData);
    } catch (error) {
      console.error('Error in fetchAPIKeys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  // Load API keys on component mount and set up auto-refresh
  useEffect(() => {
    fetchAPIKeys();
    
    // Auto-refresh every 100 seconds for less frequent updates
    const interval = setInterval(() => {
      fetchAPIKeys();
    }, 100000);
    
    return () => clearInterval(interval);
  }, []);

  const handleAddKey = async () => {
    if (!newKey.provider || !newKey.api_key_encrypted) {
      toast.error('Please fill in provider and API key');
      return;
    }

    try {
      // Use the secure function to add API key
      const { data, error } = await supabase
        .rpc('add_api_key_secure', {
          p_provider: newKey.provider,
          p_api_key: newKey.api_key_encrypted,
          p_rate_limit: newKey.rate_limit_per_minute,
          p_priority_weight: newKey.priority_weight,
          p_is_active: newKey.is_active
        });

      if (error) {
        console.error('Error adding API key:', error);
        toast.error('Failed to add API key: ' + error.message);
        return;
      }

      const result = data as { success: boolean; error?: string; key_id?: string };
      if (!result.success) {
        toast.error(result.error || 'Failed to add API key');
        return;
      }

      // Set the usage scope separately
      if (result.key_id && newKey.usage_scope) {
        const { error: scopeError } = await supabase
          .rpc('set_api_key_scope_secure', {
            p_key_id: result.key_id,
            p_usage_scope: newKey.usage_scope
          });
        
        if (scopeError) {
          console.error('Error setting scope:', scopeError);
          toast.warning('API key added but scope may not be set correctly');
        }
      }

      // Update account_name and supported_models
      if (result.key_id) {
        const supportedModelsArray = newKey.supported_models 
          ? newKey.supported_models.split(',').map(m => m.trim()).filter(Boolean)
          : null;

        const { data: updateData, error: updateError } = await supabase
          .rpc('update_api_key_account_details', {
            p_key_id: result.key_id,
            p_account_name: newKey.account_name || null,
            p_supported_models: supportedModelsArray,
            p_subscription_plan: newKey.subscription_plan || 'normal'
          });
        
        if (updateError) {
          console.error('Error setting account details:', updateError);
          toast.warning('API key added but account details may not be set correctly');
        } else {
          const updateResult = updateData as { success: boolean; error?: string };
          if (!updateResult.success) {
            toast.warning(updateResult.error || 'API key added but account details may not be set correctly');
          }
        }
      }

      setNewKey({
        provider: '',
        api_key_encrypted: '',
        rate_limit_per_minute: 50,
        priority_weight: 1,
        usage_scope: 'all',
        account_name: '',
        supported_models: '',
        subscription_plan: 'normal',
        is_active: true
      });
      setShowAddForm(false);
      await fetchAPIKeys();
      toast.success('API key added and encrypted successfully');
    } catch (error) {
      console.error('Error in handleAddKey:', error);
      toast.error('Failed to add API key');
    }
  };

  const handleEditKey = (apiKey: APIKey) => {
    setEditingKey(apiKey.id);
    setEditForm({
      provider: apiKey.provider,
      rate_limit_per_minute: apiKey.rate_limit_per_minute,
      priority_weight: apiKey.priority_weight,
      usage_scope: apiKey.usage_scope || 'all',
      account_name: apiKey.account_name || '',
      supported_models: apiKey.supported_models || [],
      subscription_plan: apiKey.subscription_plan || 'normal',
      is_active: apiKey.is_active
    });
  };

  const handleSaveKey = async () => {
    if (!editingKey) return;

    try {
      // Update using secure function
      const { data, error } = await supabase
        .rpc('update_api_key_secure', {
          p_key_id: editingKey,
          p_provider: editForm.provider,
          p_rate_limit: editForm.rate_limit_per_minute,
          p_priority_weight: editForm.priority_weight,
          p_is_active: editForm.is_active
        });

      if (error) {
        console.error('Error updating API key:', error);
        toast.error('Failed to update API key: ' + error.message);
        return;
      }

      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        toast.error(result.error || 'Failed to update API key');
        return;
      }

      // Update usage scope separately
      if (editForm.usage_scope) {
        const { error: scopeError } = await supabase
          .rpc('set_api_key_scope_secure', {
            p_key_id: editingKey,
            p_usage_scope: editForm.usage_scope
          });
        
        if (scopeError) {
          console.error('Error setting scope:', scopeError);
          toast.warning('API key updated but scope may not be set correctly');
        }
      }

      // Update account_name and supported_models
      const { data: updateData, error: updateError } = await supabase
        .rpc('update_api_key_account_details', {
          p_key_id: editingKey,
          p_account_name: editForm.account_name || null,
          p_supported_models: editForm.supported_models || null,
          p_subscription_plan: editForm.subscription_plan || 'normal'
        });
      
      if (updateError) {
        console.error('Error updating account details:', updateError);
        toast.warning('API key updated but account details may not be set correctly');
      } else {
        const updateResult = updateData as { success: boolean; error?: string };
        if (!updateResult.success) {
          toast.warning(updateResult.error || 'API key updated but account details may not be set correctly');
        }
      }

      setEditingKey(null);
      setEditForm({});
      await fetchAPIKeys();
      toast.success('API key updated successfully');
    } catch (error) {
      console.error('Error in handleSaveKey:', error);
      toast.error('Failed to update API key');
    }
  };

  const handleDeleteKey = async (id: string) => {
    if (!confirm('Are you sure you want to delete this API key?')) return;

    try {
      // Use secure function to delete API key
      const { data, error } = await supabase
        .rpc('delete_api_key_secure', {
          p_key_id: id
        });

      if (error) {
        console.error('Error deleting API key:', error);
        toast.error('Failed to delete API key');
        return;
      }

      const result = data as { success: boolean; error?: string };
      if (result && !result.success) {
        toast.error(result.error || 'Failed to delete API key');
        return;
      }

      await fetchAPIKeys();
      toast.success('API key deleted successfully');
    } catch (error) {
      console.error('Error in handleDeleteKey:', error);
      toast.error('Failed to delete API key');
    }
  };

  const toggleKeyStatus = async (id: string, currentStatus: boolean) => {
    try {
      // Use secure function to toggle API key status
      const { data, error } = await supabase
        .rpc('update_api_key_secure', {
          p_key_id: id,
          p_is_active: !currentStatus
        });

      if (error) {
        console.error('Error toggling API key status:', error);
        toast.error('Failed to update API key status: ' + error.message);
        return;
      }

      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        toast.error(result.error || 'Failed to update API key status');
        return;
      }

      await fetchAPIKeys();
      toast.success(`API key ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error in toggleKeyStatus:', error);
      toast.error('Failed to update API key status');
    }
  };

  const resetAllRPMCounters = async () => {
    try {
      const { error } = await supabase.rpc('reset_all_rpm_counters');
      
      if (error) {
        console.error('Error resetting RPM counters:', error);
        toast.error('Failed to reset RPM counters');
        return;
      }
      
      await fetchAPIKeys();
      toast.success('All RPM counters reset successfully');
    } catch (error) {
      console.error('Error in resetAllRPMCounters:', error);
      toast.error('Failed to reset RPM counters');
    }
  };

  // Manual trigger for daily reset (reactivates keys and resets daily counters)
  const runDailyResetNow = async () => {
    try {
      toast.info('Triggering daily API key reset...');
      const { data, error } = await supabase.functions.invoke('reset-api-key-daily-limits', { body: {} });
      if (error) throw error as any;
      await fetchAPIKeys();
      const result = data as any;
      if (result?.keys_still_not_reset > 0) {
        toast.warning(`Reset completed but ${result.keys_still_not_reset} keys may need attention`);
      } else {
        toast.success(result?.message || 'Daily reset executed successfully');
      }
    } catch (error: any) {
      console.error('Error running daily reset:', error);
      toast.error(error?.message || 'Failed to run daily reset');
    }
  };

  // Force reset all database counters directly
  const forceResetAllCounters = async () => {
    try {
      setForceResetting(true);
      toast.info('Force resetting all database counters...');
      
      // Direct update to reset all counters
      const { error } = await supabase
        .from('api_key_pool')
        .update({
          current_usage: 0,
          requests_this_minute: 0,
          daily_images_generated: 0,
          rpm_usage_timestamp: new Date().toISOString(),
          last_reset: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .not('id', 'is', null);

      if (error) {
        console.error('Force reset error:', error);
        toast.error('Failed to force reset: ' + error.message);
        return;
      }

      await fetchAPIKeys();
      toast.success('All database counters force-reset to 0');
    } catch (error: any) {
      console.error('Error in forceResetAllCounters:', error);
      toast.error('Failed to force reset counters');
    } finally {
      setForceResetting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Key className="w-8 h-8 mx-auto mb-4 animate-pulse text-blue-400" />
            <p className="text-white">Loading API keys...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* A4F Reset Countdown Banner - Only show when keys are exhausted */}
      {keyStats.exhausted > 0 && (
        <Card className="bg-gradient-to-r from-amber-600/30 to-red-600/30 backdrop-blur-md border-amber-500/40">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-amber-400 animate-pulse" />
                <div>
                  <p className="text-amber-200 font-medium">
                    {keyStats.exhausted} API key(s) exhausted at A4F provider level
                  </p>
                  <p className="text-amber-300/70 text-sm">
                    Keys will automatically reset at midnight UTC (5:30 AM IST)
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-amber-200/70 text-xs uppercase tracking-wide">A4F Quota Resets In</p>
                <p className="text-amber-100 text-2xl font-bold font-mono">
                  {String(a4fResetCountdown.hours).padStart(2, '0')}:
                  {String(a4fResetCountdown.minutes).padStart(2, '0')}:
                  {String(a4fResetCountdown.seconds).padStart(2, '0')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Stats Card */}
      <Card className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <div className="flex items-center">
              <Activity className="w-6 h-6 mr-2" />
              Today's API Activity
            </div>
            <Badge variant="outline" className="bg-white/10 text-white border-white/30">
              {new Date().toLocaleDateString()}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="text-white/60 text-sm">Total API Calls</p>
              <p className="text-white text-2xl font-bold">{todayStats.total_calls.toLocaleString()}</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="text-white/60 text-sm">Images Generated</p>
              <p className="text-white text-2xl font-bold">{todayStats.total_images.toLocaleString()}</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="text-white/60 text-sm flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-green-400" /> Available
              </p>
              <p className="text-green-400 text-2xl font-bold">{keyStats.available}</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="text-white/60 text-sm flex items-center gap-1">
                <Activity className="w-3 h-3 text-blue-400" /> Fresh (0 used)
              </p>
              <p className="text-blue-400 text-2xl font-bold">{keyStats.fresh}</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="text-white/60 text-sm flex items-center gap-1">
                <XCircle className="w-3 h-3 text-red-400" /> Exhausted
              </p>
              <p className="text-red-400 text-2xl font-bold">{keyStats.exhausted}</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg">
              <p className="text-white/60 text-sm">Total Keys</p>
              <p className="text-white text-2xl font-bold">{apiKeys.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys Management Card */}
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-white flex items-center">
              <Key className="w-6 h-6 mr-2" />
              API Key Management
            </CardTitle>
          <div className="flex gap-2">
            <Button
              onClick={resetAllRPMCounters}
              variant="outline"
              size="sm"
              className="bg-orange-600/20 hover:bg-orange-600/30 border-orange-600/40 text-orange-300"
            >
              Reset RPM
            </Button>
            <Button
              onClick={runDailyResetNow}
              variant="outline"
              size="sm"
              className="bg-blue-600/20 hover:bg-blue-600/30 border-blue-600/40 text-blue-300"
            >
              Run Daily Reset
            </Button>
            <Button
              onClick={forceResetAllCounters}
              variant="outline"
              size="sm"
              disabled={forceResetting}
              className="bg-red-600/20 hover:bg-red-600/30 border-red-600/40 text-red-300"
            >
              <RotateCcw className={`w-4 h-4 mr-2 ${forceResetting ? 'animate-spin' : ''}`} />
              Force DB Reset
            </Button>
            <Button
              onClick={fetchAPIKeys}
              variant="outline"
              size="sm"
              className="bg-white/10 hover:bg-white/20 border-white/20 text-white"
            >
              <Activity className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add API Key
            </Button>
          </div>
        </div>
      </CardHeader>
      
        <CardContent>
        {/* Add API Key Form */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-white/10 rounded-lg border border-white/20">
            <h3 className="text-white font-medium mb-4">Add New API Key</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                placeholder="Provider (e.g., a4f, openai)"
                value={newKey.provider}
                onChange={(e) => setNewKey({...newKey, provider: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                type="password"
                placeholder="API Key"
                value={newKey.api_key_encrypted}
                onChange={(e) => setNewKey({...newKey, api_key_encrypted: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                placeholder="Account Name (e.g., Pro Account 1)"
                value={newKey.account_name}
                onChange={(e) => setNewKey({...newKey, account_name: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                placeholder="Supported Models (comma-separated)"
                value={newKey.supported_models}
                onChange={(e) => setNewKey({...newKey, supported_models: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                type="number"
                placeholder="Rate Limit (per minute)"
                value={newKey.rate_limit_per_minute}
                onChange={(e) => setNewKey({...newKey, rate_limit_per_minute: parseInt(e.target.value) || 50})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                type="number"
                placeholder="Priority Weight (1 = highest)"
                value={newKey.priority_weight}
                onChange={(e) => setNewKey({...newKey, priority_weight: parseInt(e.target.value) || 1})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Select value={newKey.usage_scope} onValueChange={(value: 'automation' | 'dashboard' | 'all') => setNewKey({...newKey, usage_scope: value})}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (Automation + Dashboard)</SelectItem>
                  <SelectItem value="automation">Automation Only</SelectItem>
                  <SelectItem value="dashboard">Dashboard Only</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newKey.subscription_plan} onValueChange={(value) => setNewKey({...newKey, subscription_plan: value})}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ultra">Ultra</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-gray-400 mt-2">
              <p>• Leave "Supported Models" empty to support all models</p>
              <p>• Use model keys like: provider-3/gpt-image-1, provider-3/dall-e-3</p>
              <p>• Priority 1 = highest priority (used first)</p>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleAddKey} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Save API Key
              </Button>
              <Button onClick={() => setShowAddForm(false)} className="bg-gray-600 hover:bg-gray-700">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* API Keys Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-gray-300">Provider</TableHead>
                <TableHead className="text-gray-300">Account</TableHead>
                <TableHead className="text-gray-300">Models</TableHead>
                <TableHead className="text-gray-300">API Key</TableHead>
                <TableHead className="text-gray-300">Scope</TableHead>
                <TableHead className="text-gray-300">Plan</TableHead>
                <TableHead className="text-gray-300">Rate</TableHead>
                <TableHead className="text-gray-300">Priority</TableHead>
                <TableHead className="text-gray-300">Current RPM</TableHead>
                <TableHead className="text-gray-300">Reset In</TableHead>
                <TableHead className="text-gray-300">DB Counter</TableHead>
                <TableHead className="text-gray-300">Today's Images</TableHead>
                <TableHead className="text-gray-300">Success %</TableHead>
                <TableHead className="text-gray-300">Last Used</TableHead>
                <TableHead className="text-gray-300">Status</TableHead>
                <TableHead className="text-gray-300">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.map((apiKey) => (
                <TableRow key={apiKey.id} className="border-white/10">
                  <TableCell>
                    {editingKey === apiKey.id ? (
                      <Input
                        value={editForm.provider || ''}
                        onChange={(e) => setEditForm({...editForm, provider: e.target.value})}
                        className="bg-white/10 border-white/20 text-white text-sm"
                      />
                    ) : (
                      <span className="text-white font-medium">{apiKey.provider}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingKey === apiKey.id ? (
                      <Input
                        value={editForm.account_name || ''}
                        onChange={(e) => setEditForm({...editForm, account_name: e.target.value})}
                        placeholder="Account name"
                        className="bg-white/10 border-white/20 text-white text-sm"
                      />
                    ) : (
                      <span className="text-white text-sm">
                        {apiKey.account_name || <span className="text-gray-500">-</span>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingKey === apiKey.id ? (
                      <Input
                        value={Array.isArray(editForm.supported_models) ? editForm.supported_models.join(', ') : ''}
                        onChange={(e) => setEditForm({...editForm, supported_models: e.target.value.split(',').map(m => m.trim()).filter(Boolean)})}
                        placeholder="model1, model2"
                        className="bg-white/10 border-white/20 text-white text-sm"
                      />
                    ) : (
                      <div className="text-white text-xs max-w-[150px]">
                        {apiKey.supported_models && apiKey.supported_models.length > 0 ? (
                          <span className="line-clamp-2">{apiKey.supported_models.join(', ')}</span>
                        ) : (
                          <span className="text-gray-500">All models</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-gray-400 text-sm font-mono">
                      ****encrypted****
                    </div>
                  </TableCell>
                  <TableCell>
                    {editingKey === apiKey.id ? (
                      <Select 
                        value={editForm.usage_scope || 'all'} 
                        onValueChange={(value: 'automation' | 'dashboard' | 'all') => setEditForm({...editForm, usage_scope: value})}
                      >
                        <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="automation">Automation</SelectItem>
                          <SelectItem value="dashboard">Dashboard</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={
                        apiKey.usage_scope === 'all' ? 'bg-blue-600/20 text-blue-300' :
                        apiKey.usage_scope === 'automation' ? 'bg-purple-600/20 text-purple-300' :
                        'bg-cyan-600/20 text-cyan-300'
                      }>
                        {apiKey.usage_scope === 'all' ? 'All' : 
                         apiKey.usage_scope === 'automation' ? 'Automation' : 'Dashboard'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingKey === apiKey.id ? (
                      <Select 
                        value={editForm.subscription_plan || 'normal'} 
                        onValueChange={(value) => setEditForm({...editForm, subscription_plan: value})}
                      >
                        <SelectTrigger className="w-28 bg-white/10 border-white/20 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ultra">Ultra</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={
                        apiKey.subscription_plan === 'ultra' ? 'bg-gradient-to-r from-purple-600/20 to-pink-600/20 text-purple-300' :
                        apiKey.subscription_plan === 'pro' ? 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20 text-blue-300' :
                        'bg-gray-600/20 text-gray-300'
                      }>
                        {apiKey.subscription_plan === 'ultra' ? 'Ultra' : 
                         apiKey.subscription_plan === 'pro' ? 'Pro' : 'Normal'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingKey === apiKey.id ? (
                      <Input
                        type="number"
                        value={editForm.rate_limit_per_minute || ''}
                        onChange={(e) => setEditForm({...editForm, rate_limit_per_minute: parseInt(e.target.value)})}
                        className="w-20 bg-white/10 border-white/20 text-white text-sm"
                      />
                    ) : (
                      <span className="text-white">{apiKey.rate_limit_per_minute}/min</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingKey === apiKey.id ? (
                      <Input
                        type="number"
                        value={editForm.priority_weight || ''}
                        onChange={(e) => setEditForm({...editForm, priority_weight: parseInt(e.target.value)})}
                        className="w-16 bg-white/10 border-white/20 text-white text-sm"
                      />
                    ) : (
                      <span className="text-white">{apiKey.priority_weight}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-2">
                        <Activity className="w-4 h-4 text-blue-400" />
                        <span className={`text-sm font-semibold ${
                          (apiKey.requests_this_minute || 0) >= apiKey.rate_limit_per_minute * 0.8 
                            ? 'text-red-400' 
                            : (apiKey.requests_this_minute || 0) >= apiKey.rate_limit_per_minute * 0.5
                            ? 'text-yellow-400'
                            : 'text-green-400'
                        }`}>
                          {apiKey.requests_this_minute || 0}/{apiKey.rate_limit_per_minute}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {Math.round((apiKey as any).rpm_percentage || 0)}% used
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {(apiKey as any).seconds_until_reset || 0}s
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {(apiKey as any).is_exhausted_at_provider && (
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                      )}
                      <span className={`text-sm font-mono ${
                        (apiKey.daily_images_generated || 0) >= 9999 ? 'text-red-400' :
                        (apiKey.daily_images_generated || 0) >= (apiKey.daily_limit || 300) ? 'text-red-400' :
                        (apiKey.daily_images_generated || 0) > 0 ? 'text-yellow-400' : 'text-green-400'
                      }`}>
                        {(apiKey.daily_images_generated || 0) >= 9999 ? 'EXHAUSTED' : (apiKey.daily_images_generated || 0)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-white text-sm font-mono">
                      {apiKey.total_images?.toLocaleString() || '0'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      (apiKey.success_rate || 0) >= 95 ? 'bg-green-600/20 text-green-300' :
                      (apiKey.success_rate || 0) >= 80 ? 'bg-yellow-600/20 text-yellow-300' :
                      'bg-red-600/20 text-red-300'
                    }>
                      {apiKey.success_rate?.toFixed(1) || '0'}%
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-white text-xs">
                      {apiKey.last_used ? new Date(apiKey.last_used).toLocaleDateString() : 'Never'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge className={apiKey.is_active ? 'bg-green-600/20 text-green-300' : 'bg-red-600/20 text-red-300'}>
                      {apiKey.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {editingKey === apiKey.id ? (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSaveKey}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {setEditingKey(null); setEditForm({});}}
                            className="bg-gray-600 hover:bg-gray-700"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleEditKey(apiKey)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => toggleKeyStatus(apiKey.id, apiKey.is_active)}
                            className={apiKey.is_active ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
                          >
                            {apiKey.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleDeleteKey(apiKey.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {apiKeys.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">No API keys configured. Add API keys for parallel processing.</p>
          </div>
        )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAPIKeyManagement;